import type {
  GpsPermissionState,
  ScanMethod,
  ScanOutcome,
  ScanSyncItem,
  ScanSyncResult,
} from "./scan-contract";
import { createQueuedManualFallbackScan } from "../../../../packages/guard-core/src/manual-fallback-capture.ts";

/**
 * Minimal structural client for the sync_scans RPC. Using this instead of the full SupabaseClient
 * avoids the supabase-js Json index-signature constraint on interface payloads (an interface like
 * ScanSyncItem has no implicit string index signature, so it isn't assignable to the generic rpc
 * args type). The real SupabaseClient is structurally assignable to this — the capture UI passes
 * the real client. Mirrors apps/mobile's scan-sync transport.
 */
export interface ScanSyncRpcClient {
  rpc(
    functionName: "sync_scans",
    args: { active_account_id: string; scans: ScanSyncItem[] },
  ): PromiseLike<{ data: unknown; error: { message: string } | null }>;
}

/** SAFE generic submit failure — never leaks the RPC error. Online-first MVP: the offline queue
 *  (P2) will change this to "saved, we'll retry"; today a failure means try again. */
export const SAFE_SUBMIT_FAILURE = "Couldn't submit that scan — please try again.";

// crypto.randomUUID is available in secure-context browsers + the test runtime; guard for older
// runtimes. The id is an idempotency key (a UUID), never a secret.
function newClientEventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `cev-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

/** The capture facts the UI gathers; buildScanItem stamps the id + device time and shapes the item. */
export interface CaptureInput {
  token: string;
  scanMethod: ScanMethod;
  lat: number | null;
  lng: number | null;
  gpsAccuracyMeters: number | null;
  gpsPermission: GpsPermissionState;
  deviceId: string;
  appVersion: string;
  routeAssignmentId: string | null;
  checkpointId?: string;
  offlineCreated?: boolean;
}

/** Test/seam overrides so buildScanItem is deterministic when needed. */
export interface BuildScanItemOverrides {
  clientEventId?: string;
  deviceTime?: string;
}

/**
 * Assemble a sync_scans item from capture facts. Stamps a fresh clientEventId (idempotency key) and
 * device time unless overridden. NEVER carries account/user (ADR-005) — the server derives them.
 */
export function buildScanItem(
  input: CaptureInput,
  overrides: BuildScanItemOverrides = {},
): ScanSyncItem {
  const item: ScanSyncItem = {
    clientEventId: overrides.clientEventId ?? newClientEventId(),
    token: input.token,
    scanMethod: input.scanMethod,
    deviceTime: overrides.deviceTime ?? new Date().toISOString(),
    lat: input.lat,
    lng: input.lng,
    gpsAccuracyMeters: input.gpsAccuracyMeters,
    gpsPermission: input.gpsPermission,
    deviceId: input.deviceId,
    appVersion: input.appVersion,
    offlineCreated: input.offlineCreated ?? false,
    appSurface: "web",
    routeAssignmentId: input.routeAssignmentId,
  };
  if (input.checkpointId !== undefined) {
    item.checkpointId = input.checkpointId;
  }
  return item;
}

/** Build a MANUAL sync item with the verification block sync_scans requires for tokenless fallback. */
export function buildManualScanItem(
  input: Omit<CaptureInput, "token" | "scanMethod"> & { checkpointId: string },
  overrides: BuildScanItemOverrides = {},
): ScanSyncItem {
  const queued = createQueuedManualFallbackScan({
    checkpointId: input.checkpointId,
    clientEventId: overrides.clientEventId ?? newClientEventId(),
    deviceTime: overrides.deviceTime ?? new Date().toISOString(),
    deviceId: input.deviceId,
    appVersion: input.appVersion,
    networkOnline: !(input.offlineCreated ?? false),
    routeAssignmentId: input.routeAssignmentId,
    location: {
      permission: input.gpsPermission,
      lat: input.lat,
      lng: input.lng,
      accuracyMeters: input.gpsAccuracyMeters,
    },
  });
  return {
    ...queued.payload,
    appSurface: "web",
    routeAssignmentId: queued.routeAssignmentId,
  };
}

export type SubmitScanResult =
  | { ok: true; outcome: ScanOutcome; checkpointName: string | null; serverScanId: string | null }
  | { ok: false; reason: string };

/**
 * Submit ONE scan via the sync_scans RPC (active_account_id + scans[]) — the same contract the
 * native app uses; sync_scans already accepts the guard role. Returns the per-scan outcome
 * (validated/flagged/rejected/duplicate) on success; on ANY error returns the SAFE message (the RPC
 * error is never surfaced).
 */
export async function submitScan(
  client: ScanSyncRpcClient,
  activeAccountId: string,
  item: ScanSyncItem,
): Promise<SubmitScanResult> {
  try {
    const { data, error } = await client.rpc("sync_scans", {
      active_account_id: activeAccountId,
      scans: [item],
    });
    if (error !== null && error !== undefined) {
      return { ok: false, reason: SAFE_SUBMIT_FAILURE };
    }
    const result = findResult(data, item.clientEventId);
    if (result === null) {
      return { ok: false, reason: SAFE_SUBMIT_FAILURE };
    }
    return {
      ok: true,
      outcome: result.status,
      checkpointName: result.checkpointName ?? null,
      serverScanId: result.serverScanId ?? null,
    };
  } catch {
    return { ok: false, reason: SAFE_SUBMIT_FAILURE };
  }
}

/** Find this scan's result in the sync_scans response (matched by clientEventId). null if absent/malformed. */
function findResult(data: unknown, clientEventId: string): ScanSyncResult | null {
  if (typeof data !== "object" || data === null) {
    return null;
  }
  const results = (data as { results?: unknown }).results;
  if (!Array.isArray(results)) {
    return null;
  }
  for (const raw of results) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const row = raw as Record<string, unknown>;
    if (row.clientEventId !== clientEventId) {
      continue;
    }
    const status = parseOutcome(row.outcome);
    if (status === null) {
      return null;
    }
    const result: ScanSyncResult = { clientEventId, status };
    if (typeof row.checkpointName === "string" && row.checkpointName.trim().length > 0) {
      result.checkpointName = row.checkpointName.trim();
    }
    if (typeof row.serverScanId === "string") {
      result.serverScanId = row.serverScanId;
    }
    return result;
  }
  return null;
}

function parseOutcome(value: unknown): ScanOutcome | null {
  if (value === "validated" || value === "flagged" || value === "rejected" || value === "duplicate") {
    return value;
  }
  return null;
}
